import React from 'react';

import refreshListings from 'app-core/platform/listable/refreshListings';
import {
  StarrableItemsQuery,
  StarrableItemsQueryVariables,
} from 'app-core/platform/starrable/__generated/StarrableItemsQuery';
import {STARRED_ITEMS_QUERY} from 'app-core/platform/starrable/CoreObjectStarIcon';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import CalendarUtil from 'calendar/CalendarUtil';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileMenu from 'file-browser/FileMenu';
import FinderUtil from 'file-browser/FinderUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {ProjectsListingFolder, ProjectsListingProject} from 'nav/sidepanel/listing/ProjectsListing';
import StarIcon from 'nav/sidepanel/StarIcon';
import {Sort, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import {ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';
import {getColumnHeaderSortDirection} from 'ui-library/scrollable/ScrollableTableUtil';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';
import {ElementMouseEvent} from 'util/Types';

export interface ExpandedProjectTableProps {
  finderId: string;
  openResult: (rootFolder: StringIDFolder) => void;
  items: Array<ProjectsListingProject | ProjectsListingFolder>;
  areProjectsLoading: boolean;
  sorts: Array<Sort>;
  onChangeSort: (sortKey: string) => void;
}

export interface ProjectScrollableTableRow extends ScrollableTableRow {
  extra: {
    result: ProjectsListingProject;
    targetFolder: StringIDFolder;
  };
}

export default function ExpandedProjectTable(props: ExpandedProjectTableProps): JSX.Element {
  if (props.areProjectsLoading) {
    return (
      <div>
        <span className="faded">Loading...</span>
      </div>
    );
  } else if (props.items.length === 0) {
    return (
      <div>
        <span className="faded">None found.</span>
      </div>
    );
  }
  const handleOpenFolder = (row: ProjectScrollableTableRow): void => {
    if (row.extra) {
      props.openResult(row.extra.targetFolder);
    }
  };
  const handleContextMenu = (row: ProjectScrollableTableRow, e: ElementMouseEvent): void => {
    if (row.extra) {
      FileMenu.open(e, {
        file: row.extra.result,
        finderId: props.finderId,
        isListing: true,
      });
    }
  };
  const columns = [
    {
      header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
      accessorKey: 'name',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: true,
      sortDirection: getColumnHeaderSortDirection(props.sorts, SortKey.SORT_NAME_KEY),
      sortKey: SortKey.SORT_NAME_KEY,
    },
    {
      header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
      accessorKey: 'date',
      isCollapsible: false,
      isSortable: true,
      sortDirection: getColumnHeaderSortDirection(props.sorts, SortKey.SORT_CREATED_AT_KEY),
      sortKey: SortKey.SORT_CREATED_AT_KEY,
    },
    {
      header: 'Description',
      accessorKey: 'description',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
    },
  ];
  const rows = props.items.map((item) => {
    if (APIUtil.isSource(item.id)) {
      const project = item as ProjectsListingProject;
      return {
        id: project.id,
        isFaded: Boolean(project.archiveRecord) || Boolean(project.hidden),
        data: {
          date: DateUtil.readableTime(project.createdAt, {showTime: false}),
          name: (
            <span>
              {project.name}
              <ArchiveRecordLabel item={project} />
              <span className="u-mini-spacing-left">
                <StarIcon
                  onClick={async () => {
                    await FileBrowserActions.toggleProjectStar(project);
                    await doQuerySafe<StarrableItemsQuery, StarrableItemsQueryVariables>(
                      {query: STARRED_ITEMS_QUERY},
                      {variables: {ids: [project.rootFolder.api_identifier]}, fetchPolicy: 'network-only'}
                    );
                    refreshListings();
                  }}
                  starred={project.starred}
                />
              </span>
            </span>
          ),
          description: project.description,
        },
        icon: <BenchlingIcon className="scrollableTable-cell-icon" icon={ModelTypeConstants.PROJECT} />,
        extra: {
          result: project,
          targetFolder: FinderUtil.sourceToRootFolder(project),
        },
      };
    } else {
      const folder = item as ProjectsListingFolder;
      return {
        id: folder.id,
        isFaded: Boolean(folder.archiveRecord),
        data: {
          date: DateUtil.readableTime(folder.created_at, {showTime: false}),
          name: (
            <span>
              {folder.name}
              <ArchiveRecordLabel item={folder} />
            </span>
          ),
          description: folder.description,
        },
        icon: <BenchlingIcon className="scrollableTable-cell-icon" icon={ModelTypeConstants.FOLDER} />,
        extra: {
          result: folder,
          targetFolder: folder,
        },
      };
    }
  });
  return (
    <ExpandedScrollableTable
      className="u-flex-grow"
      columns={columns}
      isCompact={false}
      matchParentHeight={true}
      onRowClick={handleOpenFolder}
      onRowContextMenu={handleContextMenu}
      rows={rows}
      onChangeSort={props.onChangeSort}
    />
  );
}
